// TimeScrubber — 56px sticky strip at the bottom of the map.
//
// Shows a 24h sparkline of signal volume + a draggable handle. The handle
// position represents `asOf`; locked at the right edge means LIVE. Reduced
// motion users get the same control without the slide-in transition.

import type { intel as i } from '@eidollon/contracts';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useMemo, useRef } from 'react';

const HEIGHT = 56;
const BUCKET_COUNT = 96; // 24h / 15min

export interface TimeScrubberProps {
  signals: readonly i.IntelSignal[];
  from: Date;
  to: Date;
  asOf: Date | null;
  onAsOfChange: (next: Date | null) => void;
  onLive: () => void;
  visibleCount: number;
}

export function TimeScrubber({
  signals,
  from,
  to,
  asOf,
  onAsOfChange,
  onLive,
  visibleCount,
}: TimeScrubberProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const fromMs = from.getTime();
  const toMs = to.getTime();
  const span = Math.max(1, toMs - fromMs);

  const buckets = useMemo(() => {
    const counts = new Array<number>(BUCKET_COUNT).fill(0);
    const step = span / BUCKET_COUNT;
    let max = 0;
    for (const s of signals) {
      const ts = Date.parse(s.occurred_at);
      if (ts < fromMs || ts > toMs) continue;
      const idx = Math.min(BUCKET_COUNT - 1, Math.floor((ts - fromMs) / step));
      const next = (counts[idx] ?? 0) + 1;
      counts[idx] = next;
      if (next > max) max = next;
    }
    return { counts, max: Math.max(1, max) };
  }, [signals, fromMs, toMs, span]);

  const isLive = asOf == null;
  const handleMs = asOf?.getTime() ?? toMs;
  const handlePct = ((handleMs - fromMs) / span) * 100;

  const handleLabel = asOf ? `PAUSED at ${asOf.toISOString().slice(11, 16)} UTC` : 'LIVE';

  const onTrackClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = (e.clientX - rect.left) / Math.max(1, rect.width);
    setFromPct(pct);
  };

  const setFromPct = (pct: number) => {
    const clamped = Math.max(0, Math.min(1, pct));
    const ms = fromMs + clamped * span;
    if (clamped >= 0.999) onLive();
    else onAsOfChange(new Date(ms));
  };

  const onHandleDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    e.preventDefault();
    const move = (mv: PointerEvent) => setFromPct((mv.clientX - rect.left) / rect.width);
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: HEIGHT,
        padding: '6px 12px',
        background: 'rgba(31, 36, 42, 0.94)',
        borderTop: '1px solid var(--bd)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        zIndex: 20,
      }}
      data-component="TimeScrubber"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          color: 'var(--fg-3)',
        }}
      >
        <span>24H · {visibleCount} visible</span>
        <span
          style={{
            color: isLive ? 'var(--green)' : 'var(--amber)',
            letterSpacing: 0.6,
          }}
        >
          {isLive ? '● LIVE' : `◷ ${handleLabel}`}
        </span>
      </div>

      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label="time scrubber"
        aria-valuemin={fromMs}
        aria-valuemax={toMs}
        aria-valuenow={handleMs}
        onMouseDown={onTrackClick}
        style={{
          flex: 1,
          position: 'relative',
          cursor: 'pointer',
          background: 'var(--bg-pane-2)',
          border: '1px solid var(--bd)',
        }}
      >
        {/* sparkline bars */}
        <div
          style={{
            position: 'absolute',
            inset: 1,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 0,
          }}
        >
          {buckets.counts.map((c, i) => {
            const h = (c / buckets.max) * 100;
            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length sparkline; index IS the bin id.
                key={`b-${i}`}
                style={{
                  flex: 1,
                  height: `${h}%`,
                  background: 'var(--primary)',
                  opacity: 0.55,
                }}
              />
            );
          })}
        </div>

        {/* shaded out-of-range region (right of handle when paused) */}
        {!isLive && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${handlePct}%`,
              right: 0,
              background: 'rgba(10, 14, 20, 0.55)',
              borderLeft: '1px dashed var(--amber-line)',
            }}
          />
        )}

        {/* handle */}
        <div
          onPointerDown={onHandleDown}
          style={{
            position: 'absolute',
            top: -2,
            bottom: -2,
            left: `calc(${handlePct}% - 1px)`,
            width: 2,
            background: isLive ? 'var(--green)' : 'var(--amber)',
            cursor: 'ew-resize',
            boxShadow: isLive ? '0 0 6px var(--green)' : '0 0 6px var(--amber)',
          }}
        />
      </div>
    </div>
  );
}
